import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/shared/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { authApi } from '@/lib/api';
import useAppStore from '@/stores/appStore';
import { setUser } from '@/lib/auth';
import { 
  User, 
  Envelope, 
  Phone, 
  Shield, 
  GoogleLogo,
  FloppyDisk,
  CheckCircle,
  Bell
} from '@phosphor-icons/react';

export const UserProfilePage = () => {
  const currentUser = useAppStore((state) => state.currentUser);
  const setCurrentUser = useAppStore((state) => state.setCurrentUser);
  const tenantConfig = useAppStore((state) => state.tenantConfig);
  const brandColorAccent = tenantConfig?.brand_colour || '#002FA7';

  // Form states
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [subscriptions, setSubscriptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [linkingGoogle, setLinkingGoogle] = useState(false);

  // Password states
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [updatingPassword, setUpdatingPassword] = useState(false);

  useEffect(() => {
    if (currentUser) {
      setFullName(currentUser.full_name || '');
      setEmail(currentUser.email || '');
      setSubscriptions(currentUser.notification_subscriptions || []);
    }
  }, [currentUser]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (!fullName.trim()) {
      toast.error('Full name is required');
      return;
    }

    setSaving(true);
    try {
      const { data } = await authApi.updateMe({
        full_name: fullName.trim(),
        email: email.trim() || null
      });
      
      // Update state & localStorage
      setCurrentUser(data);
      setUser(data);
      
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile settings');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      toast.error('Password cannot be empty');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setUpdatingPassword(true);
    try {
      await authApi.updateMe({
        password: password.trim()
      });
      setPassword('');
      setConfirmPassword('');
      toast.success('Password updated successfully!');
    } catch (error) {
      console.error('Failed to update password:', error);
      toast.error(error.response?.data?.detail || 'Failed to update password');
    } finally {
      setUpdatingPassword(false);
    }
  };

  const handleMockGoogleLink = async () => {
    const mockEmail = prompt("Enter mock username to link Google Sign-In:", currentUser?.full_name?.toLowerCase().replace(/\s/g, ''));
    if (!mockEmail) return;
    
    setLinkingGoogle(true);
    try {
      const { data } = await authApi.updateMe({
        google_id: `mock_${mockEmail}`,
        email: email.trim() || `${mockEmail}@gmail.com`
      });
      setCurrentUser(data);
      setUser(data);
      toast.success('Google credentials linked successfully!');
    } catch (error) {
      console.error('Google linking failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to link Google credential');
    } finally {
      setLinkingGoogle(false);
    }
  };

  const handleGoogleCredentialResponse = async (response) => {
    setLinkingGoogle(true);
    try {
      // Decode JWT token directly in client
      const token = response.credential;
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const decoded = JSON.parse(jsonPayload);
      const sub = decoded.sub;
      const googleEmail = decoded.email;

      const { data } = await authApi.updateMe({
        google_id: sub,
        email: email.trim() || googleEmail
      });
      setCurrentUser(data);
      setUser(data);
      toast.success('Google credentials linked successfully!');
    } catch (error) {
      console.error('Google linking failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to link Google credential');
    } finally {
      setLinkingGoogle(false);
    }
  };

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId || clientId.includes("your-google-client-id")) return;
    if (currentUser?.google_id) return; // already linked

    let resizeObserver = null;

    const renderGoogleButton = () => {
      if (!window.google) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleGoogleCredentialResponse,
      });
      const btnDiv = document.getElementById('googleLinkBtn');
      if (btnDiv) {
        const containerWidth = btnDiv.offsetWidth;
        const targetWidth = containerWidth ? Math.max(200, Math.min(containerWidth, 400)) : 320;
        btnDiv.innerHTML = '';
        window.google.accounts.id.renderButton(btnDiv, {
          theme: 'outline',
          size: 'large',
          width: targetWidth,
        });
      }
    };

    const setupResizeObserver = () => {
      const btnDiv = document.getElementById('googleLinkBtn');
      if (btnDiv && window.ResizeObserver) {
        let lastWidth = btnDiv.offsetWidth;
        resizeObserver = new ResizeObserver((entries) => {
          for (let entry of entries) {
            const width = entry.contentRect.width;
            if (Math.abs(width - lastWidth) > 10) {
              lastWidth = width;
              renderGoogleButton();
            }
          }
        });
        resizeObserver.observe(btnDiv);
      }
    };

    let script = document.getElementById('google-jssdk');
    if (!script) {
      script = document.createElement('script');
      script.id = 'google-jssdk';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
      script.onload = () => {
        renderGoogleButton();
        setupResizeObserver();
      };
    } else {
      if (window.google) {
        renderGoogleButton();
        setupResizeObserver();
      } else {
        const oldOnload = script.onload;
        script.onload = (e) => {
          if (oldOnload) oldOnload(e);
          renderGoogleButton();
          setupResizeObserver();
        };
      }
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.google_id]);

  if (!currentUser) {
    return (
      <DashboardLayout role="customer">
        <div className="p-12 flex justify-center">
          <LoadingSpinner />
        </div>
      </DashboardLayout>
    );
  }

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'foc': return 'Field Ops Coordinator';
      case 'agent': return 'Support Agent';
      case 'technician': return 'Technician';
      case 'customer': return 'Customer';
      default: return role;
    }
  };

  const dashboardRole = currentUser.role || 'customer';

  return (
    <DashboardLayout role={dashboardRole}>
      <div className="p-6 lg:p-8 max-w-2xl mx-auto space-y-8 animate-fade-in">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-black tracking-tight mb-2">My Profile</h1>
          <p className="text-muted-foreground">Manage your personal settings, contact information, and authentication connections</p>
        </div>

        <Card className="p-6 border bg-white shadow-sm space-y-6">
          <div className="flex items-center gap-4 pb-4 border-b border-slate-100">
            <div 
              className="h-16 w-16 rounded-full text-white flex items-center justify-center font-bold text-2xl shadow-sm border border-white"
              style={{ backgroundColor: brandColorAccent }}
            >
              {currentUser.full_name?.charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 className="text-2xl font-black text-slate-800 tracking-tight">{currentUser.full_name}</h2>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold border border-slate-200 bg-slate-50 text-slate-600 mt-1 uppercase tracking-wider">
                <Shield size={14} className="text-slate-400" /> {getRoleLabel(currentUser.role)}
              </span>
            </div>
          </div>

          <form onSubmit={handleUpdateProfile} className="space-y-5">
            {/* Phone Number (Read Only) */}
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Phone Number (Primary Login Key)
              </Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <Input
                  value={currentUser.phone}
                  className="pl-10 h-11 border-slate-300 font-mono bg-slate-50 text-slate-500 cursor-not-allowed select-none"
                  disabled
                />
              </div>
              <p className="text-[10px] text-slate-400 font-semibold">
                Your phone number is locked as your primary identity key. Contact the administrator to change it.
              </p>
            </div>

            {/* Full Name */}
            <div className="space-y-1.5">
              <Label htmlFor="prof-name" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                Full Name *
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <Input
                  id="prof-name"
                  type="text"
                  placeholder="Enter your full name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="pl-10 h-11 border-slate-300 text-slate-900 focus:border-primary"
                  required
                />
              </div>
            </div>

            {/* Email Address */}
            <div className="space-y-1.5">
              <Label htmlFor="prof-email" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                Email Address
              </Label>
              <div className="relative">
                <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <Input
                  id="prof-email"
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11 border-slate-300 text-slate-900 focus:border-primary"
                />
              </div>
            </div>

            {/* Google Authentication Link */}
            <div className="space-y-2 pt-4 border-t border-slate-100">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                Single Sign-On Integration
              </Label>
              {currentUser.google_id ? (
                <div className="flex items-center gap-2.5 p-3.5 border border-green-200 bg-green-50/50 rounded-lg text-green-800 text-sm font-semibold">
                  <CheckCircle size={20} weight="fill" className="text-green-600" />
                  Google Workspace connected (ID: {currentUser.google_id.substring(0, 12)}...)
                </div>
              ) : (
                <div className="p-4 border border-dashed rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-slate-50/50">
                  <div>
                    <h4 className="font-bold text-slate-800 text-sm flex items-center gap-1.5">
                      <GoogleLogo size={16} weight="bold" /> Link Google Credentials
                    </h4>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Connect your Google Account to sign in with single sign-on easily next time.
                    </p>
                  </div>
                  <div className="w-full sm:w-auto">
                    {process.env.REACT_APP_GOOGLE_CLIENT_ID && !process.env.REACT_APP_GOOGLE_CLIENT_ID.includes("your-google-client-id") ? (
                      <div id="googleLinkBtn" className="w-full min-h-[40px]"></div>
                    ) : (
                      <Button
                        type="button"
                        onClick={handleMockGoogleLink}
                        disabled={linkingGoogle}
                        className="w-full sm:w-auto font-bold text-xs uppercase tracking-wider h-10 flex items-center justify-center gap-2 border-slate-300 hover:bg-slate-100"
                        variant="outline"
                      >
                        <GoogleLogo size={16} weight="bold" style={{ color: '#4285F4' }} />
                        {linkingGoogle ? 'Connecting...' : 'Link Google Account (Mock)'}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t border-slate-100 mt-6">
              <Button 
                type="submit" 
                disabled={saving}
                className="font-bold text-xs uppercase tracking-wider h-11 px-6 text-white shadow flex items-center gap-1.5"
                style={{ backgroundColor: brandColorAccent }}
              >
                <FloppyDisk size={18} weight="bold" />
                {saving ? 'Saving...' : 'Save Profile Changes'}
              </Button>
            </div>
          </form>
        </Card>

        {currentUser.role !== 'customer' && (
          <Card className="p-6 border bg-white shadow-sm space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
              <div className="p-2 bg-primary/10 rounded-sm text-primary">
                <Bell size={24} style={{ color: brandColorAccent }} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-800 tracking-tight">Notification Subscriptions</h3>
                <p className="text-xs text-slate-500 mt-0.5">Subscribe to system events to be notified via WhatsApp / Email channels</p>
              </div>
            </div>

            <div className="space-y-4">
              <label className="flex items-start gap-3 p-3.5 border rounded-lg hover:bg-slate-50/50 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={subscriptions.includes('ticket_submitted_agent')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSubscriptions(prev => [...prev, 'ticket_submitted_agent']);
                    } else {
                      setSubscriptions(prev => prev.filter(k => k !== 'ticket_submitted_agent'));
                    }
                  }}
                  className="w-4.5 h-4.5 text-primary border-slate-300 rounded mt-0.5"
                />
                <div>
                  <p className="text-sm font-bold text-slate-800">New Ticket Submission Alerts</p>
                  <p className="text-xs text-slate-500 mt-0.5">Get notified when a new service request is logged by a customer.</p>
                </div>
              </label>

              <label className="flex items-start gap-3 p-3.5 border rounded-lg hover:bg-slate-50/50 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={subscriptions.includes('sla_breached_admin')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSubscriptions(prev => [...prev, 'sla_breached_admin']);
                    } else {
                      setSubscriptions(prev => prev.filter(k => k !== 'sla_breached_admin'));
                    }
                  }}
                  className="w-4.5 h-4.5 text-primary border-slate-300 rounded mt-0.5"
                />
                <div>
                  <p className="text-sm font-bold text-slate-800">SLA Breach Alerts</p>
                  <p className="text-xs text-slate-500 mt-0.5">Get notified when a ticket breaches response or resolution SLA targets.</p>
                </div>
              </label>
            </div>

            <div className="flex justify-end pt-4 border-t border-slate-100">
              <Button
                type="button"
                onClick={async () => {
                  setSaving(true);
                  try {
                    const { data } = await authApi.updateMe({
                      notification_subscriptions: subscriptions
                    });
                    setCurrentUser(data);
                    setUser(data);
                    toast.success('Notification subscriptions updated successfully!');
                  } catch (error) {
                    console.error('Failed to update subscriptions:', error);
                    toast.error(error.response?.data?.detail || 'Failed to update subscriptions');
                  } finally {
                    setSaving(false);
                  }
                }}
                disabled={saving}
                className="font-bold text-xs uppercase tracking-wider h-11 px-6 text-white shadow flex items-center gap-1.5"
                style={{ backgroundColor: brandColorAccent }}
              >
                <FloppyDisk size={18} weight="bold" />
                {saving ? 'Saving...' : 'Save Subscriptions'}
              </Button>
            </div>
          </Card>
        )}

        {/* Security Settings (Update Password) */}
        <Card className="p-6 border bg-white shadow-sm space-y-6">
          <div className="pb-4 border-b border-slate-100">
            <h3 className="text-xl font-bold text-slate-800 tracking-tight">Security Settings</h3>
            <p className="text-xs text-slate-500">Update your account login password</p>
          </div>

          <form onSubmit={handleUpdatePassword} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="new-password" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                New Password
              </Label>
              <Input
                id="new-password"
                type="password"
                placeholder="Enter new password (min 6 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11 border-slate-300 text-slate-900 focus:border-primary animate-none"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirm-password" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                Confirm New Password
              </Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder="Re-enter new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="h-11 border-slate-300 text-slate-900 focus:border-primary animate-none"
                required
              />
            </div>

            <div className="flex justify-end pt-4 border-t border-slate-100 mt-6">
              <Button
                type="submit"
                disabled={updatingPassword}
                className="font-bold text-xs uppercase tracking-wider h-11 px-6 text-white shadow flex items-center gap-1.5"
                style={{ backgroundColor: brandColorAccent }}
              >
                <FloppyDisk size={18} weight="bold" />
                {updatingPassword ? 'Updating...' : 'Update Password'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </DashboardLayout>
  );
};
